import * as THREE from 'three';

const BASE_DISTANCE = 16;  // metres behind car
const BASE_HEIGHT   = 8;   // metres above car
const ZOOM_MIN      = 0.25; // closest view  (4m back, 2m high)
const ZOOM_MAX      = 5.0;  // widest view  (80m back, 40m high)
const POS_LERP_K    = 0.08;
const LOOK_LERP_K   = 0.12;

/**
 * Third-person follow camera with zoom support.
 * Returns { threeCamera, update(carState, dt), setZoom(f), adjustZoom(delta), zoomFactor }.
 */
export function createCamera() {
  const threeCamera = new THREE.PerspectiveCamera(
    60,
    window.innerWidth / window.innerHeight,
    0.5,
    2000,
  );

  const currentPos  = new THREE.Vector3(0, BASE_HEIGHT, BASE_DISTANCE);
  const currentLook = new THREE.Vector3(0, 1, 0);
  const desiredPos  = new THREE.Vector3();
  const desiredLook = new THREE.Vector3();
  let   firstFrame  = true;
  let   zoomFactor  = 1.0;

  function setZoom(factor) {
    zoomFactor = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, factor));
  }

  function adjustZoom(delta) {
    setZoom(zoomFactor * (1 + delta));
  }

  function update(carState, dt, elevFn) {
    const { x, z, angle, y: _y } = carState;
    const sinA = Math.sin(angle), cosA = Math.cos(angle);
    const dist  = BASE_DISTANCE * zoomFactor;
    const h     = BASE_HEIGHT   * zoomFactor;

    // Effective car height: terrain + flight altitude
    const terrainElev  = elevFn ? elevFn(x, z) : 0;
    const carElev      = terrainElev + (carState.y ?? 0);
    const camTerrElev  = elevFn ? elevFn(x - sinA * dist, z - cosA * dist) : 0;

    desiredPos.set(
      x - sinA * dist,
      Math.max(carElev + h, camTerrElev + 2), // stay above terrain
      z - cosA * dist,
    );

    // Look slightly ahead of the car (proportional to zoom so it always makes sense)
    const lookAhead = 5 * Math.min(zoomFactor, 1);
    desiredLook.set(
      x + sinA * lookAhead,
      carElev + Math.max(0.5, 1.2 * zoomFactor),
      z + cosA * lookAhead,
    );

    if (firstFrame) {
      currentPos.copy(desiredPos);
      currentLook.copy(desiredLook);
      firstFrame = false;
    } else {
      const posAlpha  = 1 - Math.pow(1 - POS_LERP_K,  dt * 60);
      const lookAlpha = 1 - Math.pow(1 - LOOK_LERP_K, dt * 60);
      currentPos.lerp(desiredPos,   posAlpha);
      currentLook.lerp(desiredLook, lookAlpha);
    }

    threeCamera.position.copy(currentPos);
    threeCamera.lookAt(currentLook);
  }

  return {
    threeCamera,
    update,
    setZoom,
    adjustZoom,
    /** Call after a world-origin shift so the camera doesn't jump. */
    shiftOrigin(dx, dz) {
      currentPos.x  -= dx;  currentPos.z  -= dz;
      currentLook.x -= dx;  currentLook.z -= dz;
    },
    get zoomFactor() { return zoomFactor; },
  };
}
